
// Vue

import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action, State } from "vuex-class";
import { isEqual, debounce, get } from "lodash";

import { validationMixin } from "vuelidate";
import { maxLength, minLength, integer, required } from "vuelidate/lib/validators";
import { MapItemTypeData, MapItemData, MapItemLocationIdentifier, MapItemLocationIdentifierOption } from "@/store/map-layers/types";
import { MapSetupLayer, MapSetupGroup, SelectionObject, MapSetupItem, IMapLayerDisplay, Devices, MapSetupLayerItemRequest } from "@/store/map-setup/types";

import vselect3 from "vselect3";
import { devices } from "@/store/devices";
import api from "@/services/api.service";
import { FeaturesList } from "@/store/types";

const SMCameras = namespace("siteMonitorCameras");
const MapLayers = namespace("mapLayers");
const MapSetup = namespace("mapSetup");
const Areas = namespace("areas");

@Component({
	components: {
		vselect3
	},
	mixins: [validationMixin],
	validations: {
		mapItemData: {
			title: {
				required,
				maxLength: maxLength(250)
			},
			minElevation: {
				integer
			},
			maxElevation: {
				integer
			}
			//extraValue: {}
		}
	}
})
export default class MapItemSetup extends Vue {
	@Prop({ default: null }) public MapItem: MapSetupItem; // The Item we are Creating / Editing
	@Prop({ default: null }) public MapLayer: MapSetupLayer; // The currently selected Layer
	@Prop({ default: null }) public MapLayersForGroup: MapSetupLayer[]; //The Map layers for the currently selected group
	@Prop({ default: null }) public PlottedItems: MapItemData[]; //The items that already exist on the map.
	@Prop({ default: null }) public UnlinkedItemsLayers: MapSetupLayer[]; //The unlinked item layers

	@MapSetup.Action deleteMapItem: (mapSetupItemRequest: MapSetupLayerItemRequest) => Promise<void>;
	@MapSetup.Action updateMapItem: (mapSetupItemRequest: MapSetupLayerItemRequest) => MapSetupItem;
	@MapSetup.Mutation addMapItemLocationIdentifier: ({ mapLayerItem }) => Promise<void>;
	@MapSetup.Mutation removeMapItemLocationIdentifier: ({ mapLayerItem, locationIdentifier }) => Promise<void>;
	@MapSetup.Mutation addUpdateMapSetupLayerItem: (mapSetupItemRequest: MapSetupLayerItemRequest) => void;
	@MapSetup.Mutation removeMapSetupItem: (mapSetupItemRequest: MapSetupLayerItemRequest) => void;
	@MapSetup.Getter getLayerItemById: (mapSetupItemRequest: MapSetupLayerItemRequest) => MapSetupItem;
	@MapLayers.Getter("getMapItemIcons") public mapIcons!: any[];
	@MapLayers.Getter("getBrokenMapItemIcons") public brokenMapIcons!: any[];
	@MapLayers.Getter("getEditableMapLayerItemTypes") public mapItemTypes: MapItemTypeData[];
	@Getter private getFeaturesList: FeaturesList;
	@Getter private getFeature: (featureName: string[]) => boolean;

	@SMCameras.Action private getDevicesByMapLayerItemType: ({
		groupID,
		deviceTypeID,
		filter
	}: {
		groupID: any;
		deviceTypeID: any;
		filter: any;
	}) => Promise<any[]>;

	@Areas.Action getResponsesByArea: any;

	private isEditMode: boolean = false;
	private isDeleteConfirmationVisible: boolean = false;

	private mapLayerDisplay: IMapLayerDisplay = { title: "", mapLayerID: 0 };
	private selectedLayers: MapSetupLayer[] = [];

	private selectedDevice: SelectionObject = null;
	private availableDevices: SelectionObject[] = [];

	private mapItemLocationIdentifiers: MapItemLocationIdentifierOption[] = [];

	private defaultLocationIdentifierType: number = 1;

	private mapItemTemplate: MapSetupItem; // The thing we validate against and reset to
	private mapItemData: MapSetupItem = {
		mapLayerItemID: 0,
		mapLayerItemTypeID: 0,
		objectID: 0,
		extraValue: "",
		title: "",
		latLong: "",
		minElevation: 0,
		maxElevation: 0,
		regionPath: "",
		mapLayerIds: [],
		saved: true,
		locationIdentifiers: [],
		brokenObjectLink: false
	};

	// For translating the mapLayerItemTypeId into a user friendly title
	typeDictionary: Map<number, string> = new Map([
		[1, "Camera"],
		[2, "Audio"],
		[3, "Door"],
		[4, "Alarm"],
		[5, "Output"],
		[6, "Geofence"]
	]);

	private onlyContainsUnlinkedLayer(layers: MapSetupLayer[]) {
		let unlinkedLayer = this.UnlinkedItemsLayers.find(layer => layer.groupId === this.MapLayer.groupId);
		if (layers.length == 1) {
			if (layers[0].mapLayerId == unlinkedLayer.mapLayerId) {
				return true;
			}
		}

		return false;
	}

	private generateLayerRequest(layers: MapSetupLayer[], mapSetupItem: MapSetupItem): MapSetupLayerItemRequest {
		return { mapSetupLayers: layers, mapSetupLayerItem: mapSetupItem } as MapSetupLayerItemRequest;
	}

	private generateLayerRequestExcludeUnlinked(mapSetupLayers: MapSetupLayer[], mapSetupItem: MapSetupItem): MapSetupLayerItemRequest {
		let unlinkedLayer = this.UnlinkedItemsLayers.find(layer => layer.groupId === this.MapLayer.groupId);
		
		let layers: MapSetupLayer[] = mapSetupLayers.filter(layer => 
			layer !== unlinkedLayer
		);

		mapSetupItem.mapLayerIds.remove(unlinkedLayer.mapLayerId);

		return { mapSetupLayers: layers, mapSetupLayerItem: mapSetupItem } as MapSetupLayerItemRequest;
	}

	/** Retrieves an icon URL for the provided item type.
	 * @param {number} itemTypeID The item type we are retrieving an icon for.
	 * @returns An Icon image source.
	 */
	private getItemIcon(itemTypeID: number): string {
		return ( this.isBrokenItem && this.showBrokenItemsFeatureEnabled) ? this.brokenMapIcons[itemTypeID] : this.mapIcons[itemTypeID];
	}

	/**
	 * Determines whether or not the item is a broken item.
	 * @returns True if item is broke, else false.
	 */
	private get isBrokenItem(): Boolean {
		return this.mapItemData.brokenObjectLink;
	}

	/**
	 * Retrieves the feature flag setting for whether or not we are displaying broken map items.
	 */
	private get showBrokenItemsFeatureEnabled(): boolean {
		return this.getFeature(["MapSetup", "ShowBrokenMapItems"]);
	}

	private getBrokenReason(deviceType: string): string {
		return `This item is broken due to a missing item link.
				<br/>
				The item link was likely lost due to a ${deviceType} deletion.
				<br/>
				Please edit this item and re-select the correct ${deviceType} or delete this map layer item.`
	}

	public async mounted() {
		// create our local mapItem object,
		if (this.MapItem) {
			this.mapItemData = { ...this.MapItem };

			//Ensure mapLayerIds is initalized
			if (!this.mapItemData.mapLayerIds) {
				this.mapItemData.mapLayerIds = [];
			}

			if (this.mapItemData.mapLayerItemID == 0) {
				// Create default values for the values we dont recieve if we are creating an object
				this.mapItemData.mapLayerItemID = 0;
				this.mapItemData.title = "untitled";
				this.mapItemData.extraValue = "";
				this.mapItemData.regionPath = "";
				this.mapItemData.objectID = 0;
				this.mapItemData.mapLayerIds = [];
				this.toggleEdit();
			}
		}

		await this.loadDevices();

		// Initialize the template the fields will reset to on reset
		this.mapItemTemplate = Object.assign({}, this.mapItemData);

		// setup for select dropdowns
		this.setDefaultDevice();
		this.setDefaultLayer();

		// call layer selected to set layer title for display
		this.setLayerDisplay();
		this.deviceFilter();

		if (this.isMobileCheckInEnabled) {
			this.mapItemLocationIdentifiers = await api.loadMapItemLocationIdentifiers();
		}
	}

	private get isMobileCheckInEnabled(): boolean {
		return get(this.getFeaturesList, ["Mobile", "CheckIn"]);
	}

	/**
	 * load the apropriate devices depending on the item type
	 */
	private async loadDevices(filter: string = null): Promise<void> {
		// Load Alarms
		if (this.mapItemData.mapLayerItemTypeID == 4) {
			// ALARM
			// Load all responses for this group
			let responses = await this.getResponsesByArea(this.MapLayer.groupId);
			// Convert to SelectionObject
			responses.forEach(response => {
				if (
					!this.availableDevices.find(
						(d: SelectionObject) => d.title == response.title && d.objectId == response.responseID
					)
				) {
					this.availableDevices.push(new SelectionObject(response, this.mapItemData.mapLayerItemTypeID));
				}
			});
		} else {
			let devices = await this.getDevicesByMapLayerItemType({
				groupID: this.MapLayer.groupId,
				deviceTypeID: this.MapItem.mapLayerItemTypeID,
				filter: filter
			});

			// Convert to SelectionObject
			devices.forEach(device => {
				if (
					!this.availableDevices.find(
						(d: SelectionObject) => d.title == device.title && d.objectId == device.deviceID
					)
				) {
					this.availableDevices.push(new SelectionObject(device, this.mapItemData.mapLayerItemTypeID));
				}
			});
		}
		this.deviceFilter();
	}

	/**
	 * Filter already assigned devices out of the list of available devices
	 */
	private deviceFilter() {
		// Get a list of Devices already assigned to a maplayer item
		let layerItems: MapSetupItem[] = this.MapLayersForGroup.flatMap((f: MapSetupLayer) => f.layerItems);

		let devices = layerItems.map((li: MapSetupItem) => {
			return { id: li.objectID, type: li.mapLayerItemTypeID };
		});

		if (this.PlottedItems) {
			let workingPlottedItems = this.isBrokenItem ? this.PlottedItems.filter(item => !item.brokenObjectLink) : this.PlottedItems;
			let filteredTable = this.availableDevices.filter(itemToFilter => {
				return !workingPlottedItems.some( (plottedItem: MapItemData) => {
					return (plottedItem.objectID == itemToFilter.objectId || plottedItem.title == itemToFilter.title) && plottedItem.mapLayerItemTypeID == itemToFilter.typeId;
				});
			});

			this.availableDevices = filteredTable;
		}

		// Filters any devices already in use not caught by the plottedItems filter.
		this.availableDevices = this.availableDevices.filter(f => {
			return !devices.some(device => {
				return device.id === f.objectId && device.type === f.typeId;
			});
		});
	}

	/**
	 * Sets the initial selected device
	 */
	private setDefaultDevice() {
		let currentGroup = this.MapLayersForGroup.find(f => f.mapLayerId == this.MapLayer.mapLayerId);

		let currentItem = currentGroup.layerItems.find(
			f => f.mapLayerItemID > 0 && f.mapLayerItemID == this.mapItemData.mapLayerItemID
		);
		if (currentItem) {
			this.selectedDevice = new SelectionObject(currentItem, 0);
		}
	}

	/**
	 * Sets the initial selected layer
	 */
	private setDefaultLayer() {
		// Populate the select layers with the layer were passed when were creating a new item
		if (this.mapItemData.mapLayerItemID == 0 && this.MapLayersForGroup) {
			if (this.MapLayersForGroup[0].mapLayerId > 0) {
				this.selectedLayers = [];
				this.selectedLayers.push(this.MapLayersForGroup.find(f => f.title == this.MapLayer.title));
			} else {
				// If were creating a new item and we have no layer dont set a default
				this.selectedLayers = [];
			}
		} else {
			// Populate the selected layers with the existing items linked layers
			let assignedLayers: MapSetupLayer[] = this.MapLayersForGroup.filter((layer: MapSetupLayer) => {
				return layer.layerItems.some(s => s.mapLayerItemID == this.mapItemData.mapLayerItemID);
			});
			this.selectedLayers = assignedLayers;
		}
	}

	/**
	 * Invokes methods that need to be called whenever a layer is linked or unlinked from/to the item.
	 */
	private linkedLayerChanged() {
		this.setLayerDisplay();
		this.addOrRemoveUnlinkedLayer();
	}

	/**
	 * Unlinks the item from the unlinked layer whenever the item is linked to another layer or links the unlinked layer whenever the item has no linked layers.
	 */
	private addOrRemoveUnlinkedLayer() {
		let unlinkedItemsLayer = this.UnlinkedItemsLayers.find(layer => layer.groupId === this.MapLayer.groupId);

		if (unlinkedItemsLayer) {
			if (this.selectedLayers.length == 0) {
				this.selectedLayers.push(unlinkedItemsLayer);
				this.setLayerDisplay();
			}
			else if (this.selectedLayers.length > 1) {
				this.selectedLayers.remove(unlinkedItemsLayer);
				this.setLayerDisplay();
			}
		}
	}

	/**
	 * Sets the layer display object for the UI
	 */
	private setLayerDisplay() {
		// If there are no layers display placeholder
		if (this.selectedLayers.length == 0) {
			this.mapLayerDisplay = { title: "No Layer Linked", mapLayerID: 0 };
		} else {
			// Set the displayLayer to be the first selection from the selected layers
			this.mapLayerDisplay.title = this.selectedLayers[0].title;
			this.mapLayerDisplay.mapLayerID = this.selectedLayers[0].mapLayerId;
		}
	}

	/**
	 * Sets the selected device to the local map object
	 */
	private deviceSelected() {
		this.mapItemData.title = this.selectedDevice.title;
		this.mapItemData.objectID = this.selectedDevice.objectId;
	}

	/**
	 * returns popup edit mode state
	 */
	private get isEditModeEnabled() {
		return this.isEditMode;
	}

	// Button functions \\
	private setRegion() {
		this.$emit("setRegion");
	}

	/**
	 * Open the delete confimation dialog
	 */
	private deleteMapLayerItemDialog() {
		this.isDeleteConfirmationVisible = true;
	}

	/**
	 * Toggles the popup edit mode
	 */
	private toggleEdit() {
		this.isEditMode = !this.isEditMode;
	}

	/**
	 * Save the local item to the DB
	 */
	private async saveMapItemDetails() {
		// Add linked map layers to the local mapitem if we have any

		let layerItemStateRequest = this.generateLayerRequest(this.MapLayersForGroup, this.mapItemData);
		let itemFromState = this.getLayerItemById(layerItemStateRequest) ?? this.mapItemData;

		let itemFromStateCopy = JSON.parse(JSON.stringify(itemFromState)) as MapSetupItem;

		let backupLayerIds = [...itemFromStateCopy.mapLayerIds];
		itemFromStateCopy.mapLayerIds = [];
		if (this.selectedLayers.length > 0) {
			this.selectedLayers.forEach(layer => itemFromStateCopy.mapLayerIds.push(layer.mapLayerId));
		}

		let removedLayerIds = backupLayerIds.filter(layer => !itemFromStateCopy.mapLayerIds.includes(layer));

		let removedLayers = this.MapLayersForGroup.filter(layer => removedLayerIds.some(layerID => layerID === layer.mapLayerId));

		let mapSetupLayerItem = itemFromStateCopy;
		let mapSetupLayers = this.selectedLayers;

		// Remove the items from the layers it has been removed from
		this.removeMapSetupItem(this.generateLayerRequest(removedLayers, mapSetupLayerItem));

		// invoke the necessary api call and update our layer items appropriately.
		// If our item is only being linked to the unlinked layer.
		if (this.onlyContainsUnlinkedLayer(mapSetupLayers)) {
			// Fire off the api to perform the necessary db actions 
			// only send a copy of our maplayer item so that the stripping of the unlinked layer does not affect the source.
			this.mapItemData = await this.updateMapItem(this.generateLayerRequestExcludeUnlinked([...mapSetupLayers], JSON.parse(JSON.stringify(mapSetupLayerItem))));
			// Update the state with our map item for every layer it is linked to.
			this.addUpdateMapSetupLayerItem(this.generateLayerRequest(mapSetupLayers, mapSetupLayerItem));
		}
		else {
			// Fire off the api to perform the necessary db actions 
			this.mapItemData = await this.updateMapItem(this.generateLayerRequestExcludeUnlinked([...mapSetupLayers], mapSetupLayerItem));
		}

		// Save the local mapItem Data to update the template
		this.mapItemTemplate = Object.assign({}, this.mapItemData);
		this.$emit("mapItemPopupClosed", { updatedItemId: this.mapItemData.mapLayerItemID, removedLayerIds: removedLayerIds });
	}

	/**
	 * Reset the UI to its default state
	 */
	private resetMapItemData() {
		// If we are creating an item close the popup on reset
		if (this.mapItemData.mapLayerItemID == 0) {
			this.$emit("mapItemPopupClosed");
		}
		this.mapItemData = Object.assign({}, this.mapItemTemplate);

		this.setDefaultDevice();
		this.setDefaultLayer();
		this.setLayerDisplay();
		this.toggleEdit();
	}

	/**
	 * Delete the local item from the db then close the delete dialog and the popup
	 */
	private async deleteMapLayerItem() {
		let mapSetupLayerItem = this.mapItemData;
		let mapSetupLayer = this.MapLayer;
		await this.deleteMapItem(this.generateLayerRequestExcludeUnlinked([mapSetupLayer], mapSetupLayerItem));

		if (this.onlyContainsUnlinkedLayer([mapSetupLayer])) {
			this.removeMapSetupItem(this.generateLayerRequest([mapSetupLayer], mapSetupLayerItem));
		}

		this.isDeleteConfirmationVisible = false;
		this.$emit("mapItemPopupClosed", { deletedItemId: this.mapItemData.mapLayerItemID });
	}

	/**
	 * debounce method to handle our waiting time when user is inputting data
	 * @param query Data that the user has inserted
	 * @param loading callback method to enable / disable loading icon
	 * @param filterName filter name to find the endpoint, this is unique.
	 */
	private debounceFilterQuery = debounce(async (query: string) => await this.loadDevices(query), 500);

	/**
	 * Method to add default values for the location identifier.
	 */
	public addLocationIdentifier() {
		if (this.mapItemData.locationIdentifiers && this.mapItemData.locationIdentifiers.length > 0) {
			return;
		}

		const mapLayerItem = this.mapItemData;
		this.addMapItemLocationIdentifier({ mapLayerItem});
	}

	/**
	 * Method to remove location identifier from map item.
	 */
	public deleteLocationIdentifier(locationIdentifier: MapItemLocationIdentifier) {
		const mapLayerItem = this.mapItemData;
		this.removeMapItemLocationIdentifier({ mapLayerItem, locationIdentifier});
	}
}
